"use client";

import { useEffect, useState } from "react";
import {
  AUDIENCE_LABELS,
  AUDIENCE_STORAGE_KEY,
  AUDIENCE_VOICES,
  pageAllowsAudienceSwitch,
  parseAudienceVoice,
  type AudienceVoice,
} from "@/lib/audienceMode";
import type { ContentBlock } from "@/types/page";

function applyAudience(voice: AudienceVoice) {
  document.querySelectorAll<HTMLElement>("[data-audience-block]").forEach((block) => {
    block.querySelectorAll<HTMLElement>("[data-audience]").forEach((node) => {
      node.hidden = node.dataset.audience !== voice;
    });
  });
}

export function AudienceSwitcher({ blocks }: { blocks: ContentBlock[] }) {
  const enabled = pageAllowsAudienceSwitch(blocks);
  const [voice, setVoice] = useState<AudienceVoice>("business");

  useEffect(() => {
    if (!enabled) return;
    const stored = parseAudienceVoice(window.localStorage.getItem(AUDIENCE_STORAGE_KEY));
    setVoice(stored);
    applyAudience(stored);
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div className="mx-auto max-w-6xl px-4 py-4">
      <div
        className="flex flex-col gap-2 rounded-[var(--border-radius)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3 sm:flex-row sm:items-center sm:justify-between"
        role="group"
        aria-label="How this page should read"
      >
        <p className="text-sm text-[var(--color-secondary)]">Read this page as</p>
        <div className="flex flex-wrap gap-2">
          {AUDIENCE_VOICES.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => {
                setVoice(option);
                window.localStorage.setItem(AUDIENCE_STORAGE_KEY, option);
                applyAudience(option);
              }}
              className={`inline-flex min-h-[44px] items-center rounded-full px-4 text-sm font-medium ${
                voice === option
                  ? "bg-[var(--color-text)] text-[var(--color-surface)]"
                  : "border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]"
              }`}
              aria-pressed={voice === option}
            >
              {AUDIENCE_LABELS[option]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
